import { Controller } from "@hotwired/stimulus";
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { post, get, destroy } from '@rails/request.js'

import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';

import FileChecksum from "../utils/file-checksum";

export default class extends Controller {
  static targets = ["dashboardTrigger"];
  static values = {
    allowedFileTypes: Array,
    directUploadUrl: String,
    dragOverTargetElementId: String,
    fieldName: String,
    maxNumberOfFiles: Number,
    targetElementId: String,
    updateUrl: String,
  }

  uppy = undefined

  connect() {
    this.uppy = this.setupUppy()
    const dashboard = this.uppy.getPlugin('Dashboard')

    if (this.hasDragOverTargetElementIdValue){
      const dragOverTarget = document.querySelector(this.dragOverTargetElementIdValue)
      
      if (dragOverTarget){
        dragOverTarget.addEventListener("dragover", (event) => {
          event.preventDefault();
          
          if (!dashboard.isModalOpen()) {
            dashboard.openModal()
          }
        })
      }
    }
  }

  setupUppy(){
    return new Uppy({
      allowMultipleUploads: true,
      autoProceed: false,
      // logger: this.debugLogger,
      restrictions: {
        allowedFileTypes: this.allowedFileTypesValue,
        maxNumberOfFiles: this.hasMaxNumberOfFilesValue ? this.maxNumberOfFilesValue : undefined,
      },
    })
      .use(AwsS3, {
        endpoint: '/uploads',
        // Generate Presigned Upload Url
        getUploadParameters: async (file) => {
          const { meta } = file
          
          const presignData = await this.presignUpload(file)

          this.uppy.setFileMeta(file.id, { ...meta, ...presignData })

          return presignData
        },
        shouldUseMultipart: (file) => {
          return file.size > 100 * 2 ** 20;
        },
        createMultipartUpload: async (file) => {
          const { data, meta, name, size, type } = file

          // Get a checksum for the file
          const checksum = await FileChecksum.create(data)

          // Get a presigned upload url from Rails
          const multipartResponse = await post('/uploads/s3/multipart', {
            body: {
              blob: {
                filename: name,
                content_type: type,
                byte_size: size,
                checksum,
              },
            },
            responseKind: 'json',
          })

          const multipartData = await multipartResponse.json
          
          this.uppy.setFileMeta(file.id, { ...meta, ...multipartData })

          return multipartData
        },
        signPart: async (file, partData) => {
          const { key, partNumber, uploadId } = partData

          const signPartResponse = await get(`/uploads/s3/multipart/${uploadId}/${partNumber}`, {
            query: {
              key,
            },
            responseKind: 'json',
          })

          return await signPartResponse.json
        },
        listParts: async (file, { uploadId, key }) => {
          const listPartsResponse = await get(`/uploads/s3/multipart/${uploadId}`, {
            query: {
              key,
            },
            responseKind: 'json',
          })

          return await listPartsResponse.json
        },
        completeMultipartUpload: async (file, { uploadId, key, parts }) => {
          const completeMultipartUploadResponse = await post(`/uploads/s3/multipart/${uploadId}/complete`, {
            body: {
              key,
              parts,
            },
            responseKind: 'json',
          })

          return await completeMultipartUploadResponse.json
        },
        abortMultipartUpload: async (file, { uploadId, key }) => {
          const abortMultipartUploadResponse = await destroy(`/uploads/s3/multipart/${uploadId}`, {
            body: {
              key,
            },
            responseKind: 'json',
          })

          return await abortMultipartUploadResponse.json
        },
      })
      .use(Dashboard, {
        showProgressDetails: true,
        target: this.hasTargetElementIdValue ? this.targetElementIdValue : document.querySelector('body'),
        theme: document.documentElement.classList.contains('light') ? 'light' : 'dark',
        trigger: this.dashboardTriggerTarget,
      })
      .on('upload-success', this.uploadSuccess.bind(this))
  }

  disconnect(){
    if (this.uppy){
      this.uppy = undefined
    }
  }

  async presignUpload(file) {
    const { data, name, size, type } = file

    // Get a checksum for the file
    const checksum = await FileChecksum.create(data)

    // Get a presigned upload url from Rails
    const presignBody = {
      blob: {
        filename: name,
        content_type: type,
        byte_size: size,
        checksum,
      },
    }
    
    const presignResponse = await post(this.directUploadUrlValue, {
      body: presignBody,
      responseKind: 'json',
    })

    // Example response from Rails
    //   {
    //     "id": 494,
    //     "key": "d255y81unzlhdx6l15j4trk9tobb",
    //     "filename": "Building a Dashboard with View Components (1).jpg",
    //     "content_type": "image/jpeg",
    //     "metadata": {},
    //     "service_name": "local",
    //     "byte_size": 585070,
    //     "checksum": "LLMSCD/kowVoq7siAkS0nQ==",
    //     "created_at": "2024-09-10T03:26:57.287Z",
    //     "attachable_sgid": "eyJfcmFpbHMiOnsiZGF0YSI6ImdpZDovL2NsaXBmbG93L0FjdGl2ZVN0b3JhZ2U6OkJsb2IvNDk0P2V4cGlyZXNfaW4iLCJwdXIiOiJhdHRhY2hhYmxlIn19--2bad84e203925046fd67af4394d98ff37d39ad45",
    //     "signed_id": "eyJfcmFpbHMiOnsiZGF0YSI6NDk0LCJwdXIiOiJibG9iX2lkIn19--a39743126898e32db6542318c210656e2babc183",
    //     "direct_upload": {
    //         "url": "http://localhost:3000/rails/active_storage/disk/eyJfcmFpbHMiOnsiZGF0YSI6eyJrZXkiOiJkMjU1eTgxdW56bGhkeDZsMTVqNHRyazl0b2JiIiwiY29udGVudF90eXBlIjoiaW1hZ2UvanBlZyIsImNvbnRlbnRfbGVuZ3RoIjo1ODUwNzAsImNoZWNrc3VtIjoiTExNU0NEL2tvd1ZvcTdzaUFrUzBuUT09Iiwic2VydmljZV9uYW1lIjoibG9jYWwifSwiZXhwIjoiMjAyNC0wOS0xMFQwMzozMTo1Ny4yOTZaIiwicHVyIjoiYmxvYl90b2tlbiJ9fQ==--2a45eef773eedda24d6c25a4dd05da55aaadaee8",
    //         "headers": {
    //             "Content-Type": "image/jpeg"
    //         }
    //     }
    // }
    const presignData = await presignResponse.json
    const { 
      direct_upload: {
        headers,
        url,
      },
    } = presignData

    // https://uppy.io/docs/aws-s3/#getuploadparametersfile-options
    return {
      ...presignData,
      headers,
      method: 'PUT',
      url,
    }
  }

  async uploadSuccess(file, response){
    const {
      meta: {
        signed_id,
      }
    } = file

    const formData = new FormData()
    formData.append(this.fieldNameValue, signed_id)

    await post(this.updateUrlValue, {
      body: formData,
      responseKind: 'turbo-stream',
    })
  }

  get debugLogger() {
    return {
      debug: (...args) => console.debug(`[Uppy]`, ...args),
      warn: (...args) => console.warn(`[Uppy]`, ...args),
      error: (...args) => console.error(`[Uppy]`, ...args),
    }
  }
}
